import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import useChatSocket from "../lib/useChatSocket";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Send,
  Users,
  MessageCircle,
  Plus,
  Lock,
  Circle,
  ChevronLeft,
  Trash2,
  Settings2,
} from "lucide-react";

const POLL_INTERVAL_MS = 15000; // fallback refresh interval

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} g`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
}

function timeHM(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

export default function ChatPage() {
  const { activeTeam } = useTeams();
  const teamId = activeTeam?.id;

  const [rooms, setRooms] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [composer, setComposer] = useState("");
  const [meId, setMeId] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(true);

  const messagesEndRef = useRef(null);
  const composerRef = useRef(null);

  // Fetch /auth/me once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMeId(data?.id);
      } catch (_) {
        /* ignore */
      }
    })();
  }, []);

  const loadRooms = useCallback(async () => {
    if (!teamId) return;
    setLoadingRooms(true);
    try {
      const { data } = await api.get(`/chat/teams/${teamId}/rooms`);
      setRooms(data || []);
      // auto-select first room if none selected
      if (!selectedRoomId && data?.length) {
        setSelectedRoomId(data[0].id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento chat");
    } finally {
      setLoadingRooms(false);
    }
  }, [teamId, selectedRoomId]);

  const loadContacts = useCallback(async () => {
    if (!teamId) return;
    try {
      const { data } = await api.get(`/chat/teams/${teamId}/contacts`);
      setContacts(data || []);
    } catch (_) {
      /* ignore */
    }
  }, [teamId]);

  useEffect(() => {
    loadRooms();
    loadContacts();
  }, [loadRooms, loadContacts]);

  // Background refresh for unread/last messages (cheap, every 15s)
  useEffect(() => {
    if (!teamId) return undefined;
    const id = setInterval(loadRooms, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [teamId, loadRooms]);

  const selectedRoom = rooms.find((r) => r.id === selectedRoomId) || null;

  const loadMessages = useCallback(async () => {
    if (!selectedRoomId) {
      setMessages([]);
      return;
    }
    setLoadingMsgs(true);
    try {
      const { data } = await api.get(
        `/chat/rooms/${selectedRoomId}/messages?limit=100`
      );
      setMessages(data || []);
    } catch (err) {
      if (err?.response?.status !== 403) {
        toast.error(err?.response?.data?.detail || "Errore caricamento messaggi");
      }
    } finally {
      setLoadingMsgs(false);
    }
  }, [selectedRoomId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleIncoming = useCallback(
    (msg) => {
      setMessages((prev) => {
        if (prev.find((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
    },
    []
  );

  const handleTyping = useCallback((payload) => {
    const uid = payload.user_id;
    if (!uid) return;
    setTypingUsers((prev) => ({ ...prev, [uid]: payload.user_name || "..." }));
    setTimeout(() => {
      setTypingUsers((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
    }, 3000);
  }, []);

  const handleError = useCallback((payload) => {
    if (payload?.error === "read_only") {
      toast.error("Questa stanza è in sola lettura per il tuo ruolo");
    }
  }, []);

  const { status: wsStatus, onlineUsers, send: wsSend, sendTyping, markRead } =
    useChatSocket(selectedRoomId, {
      onMessage: handleIncoming,
      onTyping: handleTyping,
      onError: handleError,
    });

  // Mark as read whenever messages change & WS is open
  useEffect(() => {
    if (!selectedRoomId) return;
    if (messages.length === 0) return;
    if (wsStatus === "open") {
      markRead();
    } else {
      api.post(`/chat/rooms/${selectedRoomId}/read`).catch(() => {});
    }
    // refresh unread badges
    setRooms((prev) =>
      prev.map((r) => (r.id === selectedRoomId ? { ...r, unread: 0 } : r))
    );
  }, [messages.length, selectedRoomId, wsStatus, markRead]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, selectedRoomId]);

  const myRole = activeTeam?.my_role || "owner";
  const isStaff = ["owner", "coach", "director"].includes(myRole);

  const canSend = useMemo(() => {
    if (!selectedRoom) return false;
    if (selectedRoom.write_permission === "staff_only" && !isStaff) return false;
    return true;
  }, [selectedRoom, isStaff]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const text = composer.trim();
    if (!text || !selectedRoomId) return;
    setComposer("");
    // Try WS first; fall back to REST
    const ok = wsSend(text);
    if (!ok) {
      try {
        const { data } = await api.post(
          `/chat/rooms/${selectedRoomId}/messages`,
          { content: text }
        );
        handleIncoming(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Invio fallito");
        setComposer(text);
      }
    }
    composerRef.current?.focus();
  };

  const handleStartDM = async (targetUserId) => {
    try {
      const { data } = await api.post(`/chat/rooms`, {
        team_id: teamId,
        type: "direct",
        target_user_id: targetUserId,
      });
      await loadRooms();
      setSelectedRoomId(data.id);
      setNewDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile creare chat");
    }
  };

  const handleCreateGroup = async ({ name, members, writePerm }) => {
    try {
      const { data } = await api.post(`/chat/rooms`, {
        team_id: teamId,
        type: "team_group",
        name,
        members,
        write_permission: writePerm,
      });
      await loadRooms();
      setSelectedRoomId(data.id);
      setNewDialogOpen(false);
      toast.success("Gruppo creato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile creare gruppo");
    }
  };

  const handleDeleteRoom = async (room) => {
    if (room.default_room) {
      toast.error("Il gruppo squadra principale non può essere eliminato");
      return;
    }
    if (!window.confirm(`Eliminare definitivamente "${room.display_name}"?`)) return;
    try {
      await api.delete(`/chat/rooms/${room.id}`);
      setRooms((prev) => prev.filter((r) => r.id !== room.id));
      if (selectedRoomId === room.id) setSelectedRoomId(null);
      toast.success("Stanza eliminata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eliminazione fallita");
    }
  };

  const handleUpdatePermission = async (room, perm) => {
    try {
      const { data } = await api.patch(`/chat/rooms/${room.id}`, {
        write_permission: perm,
      });
      setRooms((prev) =>
        prev.map((r) => (r.id === room.id ? { ...r, ...data } : r))
      );
      toast.success("Permessi aggiornati");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aggiornamento fallito");
    }
  };

  if (!teamId) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-slate-400">
        Seleziona una squadra per usare la chat
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] md:h-[calc(100vh-4rem)] -mt-2 md:-mt-4 rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
      {/* Sidebar rooms */}
      <aside
        className={`${
          selectedRoomId && !sidebarMobileOpen ? "hidden" : "flex"
        } md:flex flex-col w-full md:w-80 lg:w-96 border-r border-slate-200 bg-slate-50/50`}
        data-testid="chat-room-list"
      >
        <div className="p-4 border-b border-slate-200 bg-white flex items-center justify-between gap-2">
          <div>
            <div className="font-heading font-bold text-lg leading-none">Chat</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {rooms.length} {rooms.length === 1 ? "stanza" : "stanze"}
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setNewDialogOpen(true)}
            data-testid="chat-new-btn"
            className="gap-1"
          >
            <Plus size={16} /> Nuova
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loadingRooms && rooms.length === 0 && (
            <div className="p-6 text-center text-slate-400 text-sm">Caricamento…</div>
          )}
          {rooms.map((r) => {
            const isActive = r.id === selectedRoomId;
            const preview = r.last_message
              ? `${
                  r.type === "direct" ? "" : (r.last_message.sender_name || "") + ": "
                }${r.last_message.content}`
              : r.type === "direct"
              ? "Inizia la conversazione…"
              : "Nessun messaggio";
            return (
              <button
                key={r.id}
                onClick={() => {
                  setSelectedRoomId(r.id);
                  setSidebarMobileOpen(false);
                }}
                data-testid={`chat-room-${r.id}`}
                className={`w-full flex items-start gap-3 p-3 border-b border-slate-100 hover:bg-white transition text-left ${
                  isActive ? "bg-white" : ""
                }`}
              >
                <div
                  className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 text-white font-bold ${
                    r.type === "direct" ? "bg-emerald-500" : "bg-sky-500"
                  }`}
                >
                  {r.type === "direct" ? (
                    initials(r.display_name)
                  ) : (
                    <Users size={18} />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm truncate">
                      {r.display_name}
                    </div>
                    <div className="text-[10px] text-slate-400 shrink-0">
                      {relativeTime(r.last_message?.created_at || r.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <div className="text-xs text-slate-500 truncate flex-1">
                      {preview}
                    </div>
                    {r.unread > 0 && (
                      <Badge
                        variant="default"
                        className="bg-sky-500 hover:bg-sky-500 h-5 px-1.5 text-[10px]"
                        data-testid={`chat-unread-${r.id}`}
                      >
                        {r.unread}
                      </Badge>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
          {!loadingRooms && rooms.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400">
              Nessuna stanza ancora.
            </div>
          )}
        </div>
      </aside>

      {/* Conversation pane */}
      <section
        className={`${
          selectedRoomId && !sidebarMobileOpen ? "flex" : "hidden md:flex"
        } flex-col flex-1 min-w-0 bg-white`}
        data-testid="chat-thread"
      >
        {selectedRoom ? (
          <>
            <div className="border-b border-slate-200 p-3 flex items-center gap-3">
              <button
                className="md:hidden h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"
                onClick={() => setSidebarMobileOpen(true)}
                data-testid="chat-back-btn"
              >
                <ChevronLeft size={18} />
              </button>
              <div
                className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 text-white font-bold ${
                  selectedRoom.type === "direct" ? "bg-emerald-500" : "bg-sky-500"
                }`}
              >
                {selectedRoom.type === "direct" ? (
                  initials(selectedRoom.display_name)
                ) : (
                  <Users size={18} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold truncate">
                  {selectedRoom.display_name}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-2 truncate">
                  <span className="flex items-center gap-1">
                    <Circle
                      size={8}
                      className={
                        wsStatus === "open"
                          ? "fill-emerald-500 text-emerald-500"
                          : "fill-amber-500 text-amber-500"
                      }
                    />
                    {wsStatus === "open" ? "online" : "in attesa…"}
                  </span>
                  <span>•</span>
                  <span>{(selectedRoom.members || []).length} membri</span>
                  {selectedRoom.write_permission === "staff_only" && (
                    <span className="flex items-center gap-1 text-amber-600">
                      <Lock size={11} /> staff
                    </span>
                  )}
                </div>
              </div>
              {isStaff && selectedRoom.type === "team_group" && (
                <button
                  className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"
                  onClick={() => setSettingsOpen(true)}
                  data-testid="chat-settings-btn"
                  title="Impostazioni stanza"
                >
                  <Settings2 size={16} />
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50/40">
              {loadingMsgs && messages.length === 0 && (
                <div className="text-center text-slate-400 text-sm py-10">
                  Caricamento…
                </div>
              )}
              {messages.map((m, idx) => {
                const mine = m.sender_id === meId;
                const prev = messages[idx - 1];
                const showHeader =
                  !prev || prev.sender_id !== m.sender_id ||
                  new Date(m.created_at) - new Date(prev.created_at) > 5 * 60 * 1000;
                return (
                  <div
                    key={m.id}
                    className={`flex ${mine ? "justify-end" : "justify-start"}`}
                    data-testid={`chat-msg-${m.id}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-3 py-2 shadow-sm ${
                        mine
                          ? "bg-sky-500 text-white rounded-br-md"
                          : "bg-white text-slate-800 border border-slate-200 rounded-bl-md"
                      }`}
                    >
                      {!mine && showHeader && (
                        <div className="text-[11px] font-semibold mb-0.5 opacity-80">
                          {m.sender_name}
                        </div>
                      )}
                      <div className="whitespace-pre-wrap break-words text-sm leading-snug">
                        {m.content}
                      </div>
                      <div
                        className={`text-[10px] mt-1 text-right ${
                          mine ? "text-sky-100" : "text-slate-400"
                        }`}
                      >
                        {timeHM(m.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
              {!loadingMsgs && messages.length === 0 && (
                <div className="text-center text-slate-400 text-sm py-10">
                  Ancora nessun messaggio. Apri tu la conversazione!
                </div>
              )}
              {Object.keys(typingUsers).length > 0 && (
                <div className="text-xs text-slate-400 italic px-2">
                  {Object.values(typingUsers).join(", ")} sta scrivendo…
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form
              onSubmit={handleSubmit}
              className="border-t border-slate-200 p-3 bg-white flex items-end gap-2"
              data-testid="chat-composer"
            >
              <Textarea
                ref={composerRef}
                value={composer}
                onChange={(e) => {
                  setComposer(e.target.value);
                  sendTyping();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder={
                  canSend
                    ? "Scrivi un messaggio…"
                    : "Stanza in sola lettura per il tuo ruolo"
                }
                disabled={!canSend}
                rows={1}
                className="resize-none min-h-[44px] max-h-32"
                data-testid="chat-composer-input"
              />
              <Button
                type="submit"
                size="icon"
                disabled={!canSend || !composer.trim()}
                data-testid="chat-send-btn"
                className="h-11 w-11 shrink-0"
              >
                <Send size={18} />
              </Button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-2">
            <MessageCircle size={48} />
            <div className="text-sm">Seleziona una conversazione per iniziare</div>
          </div>
        )}
      </section>

      <NewChatDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        contacts={contacts}
        isStaff={isStaff}
        onStartDM={handleStartDM}
        onCreateGroup={handleCreateGroup}
      />

      {selectedRoom && (
        <RoomSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          room={selectedRoom}
          onChangePermission={(perm) => handleUpdatePermission(selectedRoom, perm)}
          onDelete={() => {
            setSettingsOpen(false);
            handleDeleteRoom(selectedRoom);
          }}
        />
      )}
    </div>
  );
}

function NewChatDialog({
  open,
  onOpenChange,
  contacts,
  isStaff,
  onStartDM,
  onCreateGroup,
}) {
  const [mode, setMode] = useState("direct"); // direct | group
  const [groupName, setGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [writePerm, setWritePerm] = useState("all");

  useEffect(() => {
    if (!open) {
      setMode("direct");
      setGroupName("");
      setSelectedMembers([]);
      setWritePerm("all");
    }
  }, [open]);

  const toggleMember = (id) => {
    setSelectedMembers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="chat-new-dialog">
        <DialogHeader>
          <DialogTitle>Nuova conversazione</DialogTitle>
          <DialogDescription>
            Avvia un messaggio diretto o crea un gruppo personalizzato per i
            membri della squadra.
          </DialogDescription>
        </DialogHeader>
        {isStaff && (
          <div className="flex gap-2 mb-2">
            <Button
              size="sm"
              variant={mode === "direct" ? "default" : "outline"}
              onClick={() => setMode("direct")}
              data-testid="chat-new-mode-direct"
              className="flex-1"
            >
              <MessageCircle size={14} className="mr-1" /> Messaggio diretto
            </Button>
            <Button
              size="sm"
              variant={mode === "group" ? "default" : "outline"}
              onClick={() => setMode("group")}
              data-testid="chat-new-mode-group"
              className="flex-1"
            >
              <Users size={14} className="mr-1" /> Gruppo custom
            </Button>
          </div>
        )}

        {mode === "direct" ? (
          <div className="max-h-80 overflow-y-auto -mx-2 px-2 space-y-1">
            {contacts.length === 0 && (
              <div className="text-sm text-slate-400 text-center py-6">
                Nessun contatto disponibile. Invita prima dei membri alla
                squadra.
              </div>
            )}
            {contacts.map((c) => (
              <button
                key={c.id}
                onClick={() => onStartDM(c.id)}
                data-testid={`chat-contact-${c.id}`}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 text-left"
              >
                <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-600">
                  {initials(c.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.role || "membro"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Nome del gruppo
              </label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Es. Annunci genitori"
                className="mt-1"
                data-testid="chat-group-name"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Chi può scrivere
              </label>
              <Select value={writePerm} onValueChange={setWritePerm}>
                <SelectTrigger
                  className="mt-1"
                  data-testid="chat-group-permission"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i membri</SelectItem>
                  <SelectItem value="staff_only">
                    Solo staff (modello annunci)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Membri ({selectedMembers.length} selezionati)
              </label>
              <div className="mt-1 max-h-48 overflow-y-auto space-y-1 border border-slate-200 rounded-lg p-1">
                {contacts.length === 0 && (
                  <div className="text-xs text-slate-400 text-center py-4">
                    Nessun membro disponibile
                  </div>
                )}
                {contacts.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(c.id)}
                      onChange={() => toggleMember(c.id)}
                      data-testid={`chat-member-check-${c.id}`}
                    />
                    <div className="text-sm">{c.name}</div>
                    <Badge variant="outline" className="ml-auto text-[10px]">
                      {c.role}
                    </Badge>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="chat-new-cancel"
          >
            Annulla
          </Button>
          {mode === "group" && (
            <Button
              onClick={() =>
                onCreateGroup({
                  name: groupName,
                  members: selectedMembers,
                  writePerm,
                })
              }
              disabled={!groupName.trim() || selectedMembers.length < 1}
              data-testid="chat-new-create-group"
            >
              Crea gruppo
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoomSettingsDialog({
  open,
  onOpenChange,
  room,
  onChangePermission,
  onDelete,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="chat-settings-dialog">
        <DialogHeader>
          <DialogTitle>Impostazioni stanza</DialogTitle>
          <DialogDescription>
            Configura permessi e gestisci la stanza chat.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Permessi di scrittura
            </label>
            <Select
              value={room.write_permission}
              onValueChange={onChangePermission}
            >
              <SelectTrigger
                className="mt-1"
                data-testid="chat-settings-permission"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i membri</SelectItem>
                <SelectItem value="staff_only">Solo staff (annunci)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="border-t border-slate-200 pt-3">
            <div className="text-xs text-slate-500 mb-1">Membri</div>
            <div className="text-sm text-slate-700">
              {(room.members || []).length} membri
            </div>
          </div>
          {!room.default_room && (
            <Button
              variant="destructive"
              onClick={onDelete}
              data-testid="chat-settings-delete"
              className="w-full"
            >
              <Trash2 size={16} className="mr-2" />
              Elimina stanza
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
